import { Link } from "react-router-dom";
import { ArrowUp, ArrowUpRight, Facebook, Twitter, Youtube, Mail, Send } from "lucide-react";
import { Logo } from "./Logo";

const COLLECTION = [
  ["All Books", "/books"],
  ["All Writings", "/writings"],
  ["About the Archive", "/about"],
  ["Contact", "/contact"],
];
const TOPICS = [
  ["Devata & Apsara", "/writings"],
  ["Khmer Kings", "/books"],
  ["Royal Ballet", "/writings"],
  ["French Indochina", "/books"],
  ["Book Reviews", "/writings"],
];
const SOCIALS = [
  { icon: Facebook, href: "https://www.facebook.com/devata.org", label: "Facebook" },
  { icon: Twitter, href: "https://twitter.com/devataorg", label: "Twitter" },
  { icon: Youtube, href: "https://www.youtube.com/user/devataorg", label: "YouTube" },
  { icon: Mail, href: "mailto:archive@devata-datasia.org", label: "Email" },
];

export const Footer = () => {
  const scrollTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer-section"
      className="relative bg-[#F5EDDC] text-[#2A2520] border-t border-[#2A2520]/10"
    >
      {/* grain / parchment feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(#2A2520 1px, transparent 1px)",
          backgroundSize: "4px 4px",
        }}
      />

      {/* CTA band */}
      <div className="relative border-b border-[#2A2520]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B47B2F]">
              Devata &amp; DatAsia · Archive Dispatch
            </p>
            <h3 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#2A2520] max-w-2xl">
              Letters from the <span className="italic text-[#A05A3A]">sacred archive</span>, delivered quarterly.
            </h3>
          </div>
          <div className="lg:col-span-5">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex items-center gap-2 bg-white border border-[#2A2520]/15 rounded-full p-1.5 pl-5 shadow-[0_1px_0_rgba(42,37,32,0.04)]"
            >
              <input
                type="email"
                placeholder="your.email@domain.com"
                data-testid="footer-newsletter-input"
                className="flex-1 bg-transparent font-sans text-sm text-[#2A2520] placeholder:text-[#2A2520]/40 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#2A2520] text-[#F5EDDC] px-5 py-2.5 font-sans text-sm font-semibold hover:bg-[#A05A3A] transition-colors"
              >
                Subscribe <Send className="h-3.5 w-3.5" />
              </button>
            </form>
            <p className="mt-3 font-sans text-xs text-[#2A2520]/60">
              Four letters a year. New books, essays and field notes. No spam.
            </p>
          </div>
        </div>
      </div>

      {/* Columns */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <Logo />
            <p className="mt-6 max-w-sm font-sans text-sm leading-relaxed text-[#2A2520]/80">
              A combined archive of Devata.org and DatAsia Editions — dedicated
              to the sacred art, history and literature of Cambodia and
              Southeast Asia.
            </p>
            <div className="mt-7 flex items-center gap-3">
              {SOCIALS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="h-11 w-11 grid place-items-center rounded-full border border-[#2A2520]/20 text-[#2A2520] hover:bg-[#2A2520] hover:text-[#F5EDDC] hover:border-[#2A2520] transition-colors duration-300"
                >
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B47B2F]">
              Collection
            </h4>
            <ul className="mt-5 space-y-3.5">
              {COLLECTION.map(([label, to]) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="group inline-flex items-center gap-1.5 font-sans text-sm text-[#2A2520]/90 hover:text-[#A05A3A] transition-colors"
                  >
                    {label}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B47B2F]">
              Topics
            </h4>
            <ul className="mt-5 space-y-3.5">
              {TOPICS.map(([label, to]) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="group inline-flex items-center gap-1.5 font-sans text-sm text-[#2A2520]/90 hover:text-[#A05A3A] transition-colors"
                  >
                    {label}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Watermark */}
        <div className="mt-16 overflow-hidden">
          <div className="font-serif text-[17vw] lg:text-[12rem] leading-[0.85] tracking-tight text-[#2A2520]/[0.07] select-none pointer-events-none whitespace-nowrap">
            DEVATA · DATASIA
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-[#2A2520]/15 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <p className="font-sans text-xs text-[#2A2520]/70">
            © {new Date().getFullYear()} Devata &amp; DatAsia Archive · As an
            Amazon Associate we may earn from qualifying purchases.
          </p>
          <button
            data-testid="back-to-top-button"
            onClick={scrollTop}
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#2A2520] hover:text-[#A05A3A] transition-colors"
          >
            Back to top
            <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </footer>
  );
};
